import {
  Button,
  createStyles,
  FormControl,
  InputAdornment,
  InputLabel,
  ListSubheader,
  makeStyles,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core'
import React, { FunctionComponent, useEffect, useState } from 'react'
import SearchIcon from '@material-ui/icons/Search'
import { getNursesDiagnosisTask } from '../../../api/chatApi/chatApi'

const useStyles = makeStyles(theme =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
    columnWrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    diagnosisRow: {
      width: '100%',
      marginRight: 1,
      marginTop: 20,
      display: 'flex',
      flexDirection: 'row',
    },
    formControl: {
      width: '100%',
    },
    inputLabel: {
      top: '-13px',
      left: '13px',
    },
    textField: {
      width: '100%',
    },
    select: {
      height: '35px',
      display: 'flex',
      alignItems: 'center',
      textOverflow: 'ellipsis',
      '& .MuiSelect-root': {
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
      '& .MuiInputBase-root': {
        height: '55px',
        display: 'flex',
        alignItems: 'center',
      },
      '& .MuiOutlinedInput-input': {
        padding: '8px',
        textOverflow: 'ellipsis'
      },
    },
    button: {
      height: 35,
      minWidth: 30,
      marginTop: 20,
      marginLeft: 10,
    },
  })
);


interface INursesDiagnosis {
  id?: number,
  nursesDiagnosis: string,
  nursesDiagnosisId: number | null,
  groupMedicineId?: number,
  groupMedicineName?: string,
  typeOfInformationLevelId?: number,
}

interface INursesDiagnosisTask {
  id: number;
  diagnosis: string;
}

interface INursesDiagnosisResponse {
  success: boolean;
  result: string;
  description: string | null;
  NursesDiagnosisTask: INursesDiagnosisTask[];
}

type Props = {
  readOnly?: boolean;
  saveMainNursesDiagnosis: (mainNursesDiagnosis: INursesDiagnosis[]) => void;
  initialDiagnosis?: INursesDiagnosis[];
  taskProceedToggle: boolean;
  preDiagnosisStatus?: boolean
};

const MainNursesDiagnosisField: FunctionComponent<Props> = props => {
  const classes = useStyles();

  const [diagnoses, setDiagnoses] = useState<INursesDiagnosis[]>(
    props.initialDiagnosis && props.initialDiagnosis.length > 0
      ? props.initialDiagnosis
      : [{
        nursesDiagnosis: '',
        nursesDiagnosisId: 0,
        groupMedicineId: 0,
        groupMedicineName: '',
        typeOfInformationLevelId: 4,
      }]
  );

  const [searchNursesDiagnosis, setSearchNursesDiagnosis] = useState<string[]>(['']);
  const [nursesDiagnosisTasks, setNursesDiagnosisTasks] = useState<INursesDiagnosisTask[]>([]);
  const [usedDiagnosisIds, setUsedDiagnosisIds] = useState<Set<number>>(new Set());

  const getNursesDiagnosis = async () => {
    try {
      const axiosResponse = await getNursesDiagnosisTask({ diagnosis: '' });
      const responseData = axiosResponse.data as unknown as INursesDiagnosisResponse;
      if (responseData?.NursesDiagnosisTask) {
        setNursesDiagnosisTasks(responseData.NursesDiagnosisTask);
      }
      return responseData;
    } catch (error) {
      console.warn('error getNursesDiagnosis', error);
    }
  }

  useEffect(() => {
    getNursesDiagnosis();
  }, []);

  const handleSearchNursesDiagnosisChange = (index: number, event: any) => {
    const updatedSearches = [...searchNursesDiagnosis];
    updatedSearches[index] = event.target.value;
    setSearchNursesDiagnosis(updatedSearches);
  };

  const containsText = (text: string, searchText: string) => {
    if (!searchText || typeof searchText !== 'string') {
      return true;
    }
    return text.toLowerCase().includes(searchText.toLowerCase());
  };

  const filteredNursesDiagnoses = (index: number) => {
    return nursesDiagnosisTasks.filter((type) =>
      containsText(type.diagnosis || '', searchNursesDiagnosis[index] || '') &&
      !usedDiagnosisIds.has(type.id)
    );
  };

  const changeMainNursesDiagnosis = async (
    index: number,
    nursesDiagnosis: string,
    nursesDiagnosisId: number,
  ) => {
    const updatedDiagnoses = [...diagnoses];
    const oldDiagnosisId = updatedDiagnoses[index].nursesDiagnosisId;
    
    if (oldDiagnosisId) {
      setUsedDiagnosisIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(oldDiagnosisId);
        return newSet;
      });
    }

    setUsedDiagnosisIds(prev => new Set(prev).add(nursesDiagnosisId));
    
    updatedDiagnoses[index] = {
      ...updatedDiagnoses[index],
      nursesDiagnosis: nursesDiagnosis,
      nursesDiagnosisId: nursesDiagnosisId,
    };
    setDiagnoses(updatedDiagnoses);
    await saveNursesDiagnosis(updatedDiagnoses);
  };
  
  const saveNursesDiagnosis = async (updatedDiagnoses: INursesDiagnosis[]) => {
    if (updatedDiagnoses) {
      props.saveMainNursesDiagnosis(updatedDiagnoses);
    } else {
      console.warn('error saveNursesDiagnosis');
    }
  };

  const addNewDiagnosisField = () => {
    const unusedDiagnoses = nursesDiagnosisTasks.filter(type => !usedDiagnosisIds.has(type.id));
    if (unusedDiagnoses.length === 0) {
      return;
    }
    
    setDiagnoses([
      ...diagnoses,
      {
        nursesDiagnosis: '',
        nursesDiagnosisId: 0,
        groupMedicineId: 0,
        groupMedicineName: '',
        typeOfInformationLevelId: 4,
      }
    ]);
    setSearchNursesDiagnosis([...searchNursesDiagnosis, '']);
  };

  useEffect(() => {
    if (Array.isArray(props.initialDiagnosis) && props.initialDiagnosis && props.initialDiagnosis.length > 0) {
      const formattedDiagnoses = props.initialDiagnosis.map(diagnosis => ({
        ...diagnosis,
        nursesDiagnosisId: diagnosis.nursesDiagnosisId || 0,
        nursesDiagnosis: diagnosis.nursesDiagnosis || '',
        typeOfInformationLevelId: diagnosis.typeOfInformationLevelId || 4,
      }));
      setDiagnoses(formattedDiagnoses);
      
      const initialUsedIds = new Set(formattedDiagnoses
        .filter(d => d.nursesDiagnosisId)
        .map(d => d.nursesDiagnosisId));
      setUsedDiagnosisIds(initialUsedIds);
    }
  }, [props.initialDiagnosis]);

  useEffect(() => {
    if (props.taskProceedToggle && props.initialDiagnosis) {
      setDiagnoses(props.initialDiagnosis);
    }
  }, [props.taskProceedToggle, props.initialDiagnosis]);

  return (
    <div className={classes.container}>
      <div className={classes.columnWrapper}>
        {diagnoses.map((diagnosis, index) => (
          <div key={index} className={classes.diagnosisRow}>
            <FormControl className={classes.formControl}>
              <InputLabel style={{overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', width: '100%'}} className={classes.inputLabel}>
                {/* {'Диагноз медсестры'} */}
                {'Предварительный диагноз'}
              </InputLabel>
              <Select
                className={classes.select}
                variant="outlined"
                // name={'Диагноз медсестры'}
                // label={'Диагноз медсестры'}
                name={'Предварительный диагноз'}
                label={'Предварительный диагноз'}
                renderValue={() => diagnosis.nursesDiagnosis || 'Выберите диагноз'}
                value={diagnosis.nursesDiagnosisId || ''}
                fullWidth
                disabled={props.taskProceedToggle}
              >
                <ListSubheader>
                  <TextField
                    value={searchNursesDiagnosis[index]}
                    size="medium"
                    autoFocus
                    placeholder="Введите диагноз"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                    onChange={(event) => handleSearchNursesDiagnosisChange(index, event)}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(event) => {
                      event.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </ListSubheader>
                {filteredNursesDiagnoses(index).map((type) => (
                  <MenuItem
                    value={type.id.toString()}
                    key={type.id}
                    onClick={() => changeMainNursesDiagnosis(index, type.diagnosis, type.id)}
                  >
                    {type.diagnosis}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        ))}
      </div>
      {!props.taskProceedToggle && nursesDiagnosisTasks.some(type => !usedDiagnosisIds.has(type.id)) ? (
        <Button className={classes.button} variant="outlined" color="primary" onClick={addNewDiagnosisField}>+</Button>
      ) : null}
    </div>
  );
};

export default MainNursesDiagnosisField;
